import { useState } from "react";
import { PawPrint, User } from "lucide-react";
import { useTranslation } from "react-i18next";
import { QuickEditPetDialog } from "@/components/pets/QuickEditPetDialog";
import { QuickEditOwnerDialog } from "@/components/owners/QuickEditOwnerDialog";
import { Button } from "@akuna/ui";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface MobilePatientEditFlowProps {
  petId: string;
  ownerId?: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved?: () => void;
}

type EditTarget = "pet" | "owner";

export function MobilePatientEditFlow({
  petId,
  ownerId,
  open,
  onOpenChange,
  onSaved,
}: MobilePatientEditFlowProps) {
  const { t } = useTranslation("medicalRecords");
  const [target, setTarget] = useState<EditTarget | null>(null);

  const hasOwner = Boolean(ownerId);

  function handleSelect(choice: EditTarget) {
    onOpenChange(false);
    setTarget(choice);
  }

  function handleEditDialogClose(isOpen: boolean) {
    if (!isOpen) setTarget(null);
  }

  if (!hasOwner) {
    return (
      <QuickEditPetDialog
        petId={petId}
        open={open}
        onOpenChange={onOpenChange}
        onSaved={onSaved}
      />
    );
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-xs gap-4">
          <DialogHeader>
            <DialogTitle>{t("quickEdit.selectDialog.title")}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <Button
              variant="outline"
              className="h-14 justify-start gap-3 text-base"
              onClick={() => handleSelect("pet")}
            >
              <PawPrint className="h-5 w-5 text-muted-foreground" />
              {t("quickEdit.selectDialog.editPet")}
            </Button>
            <Button
              variant="outline"
              className="h-14 justify-start gap-3 text-base"
              onClick={() => handleSelect("owner")}
            >
              <User className="h-5 w-5 text-muted-foreground" />
              {t("quickEdit.selectDialog.editOwner")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <QuickEditPetDialog
        petId={petId}
        open={target === "pet"}
        onOpenChange={handleEditDialogClose}
        onSaved={onSaved}
      />

      <QuickEditOwnerDialog
        ownerId={ownerId!}
        open={target === "owner"}
        onOpenChange={handleEditDialogClose}
        onSaved={onSaved}
      />
    </>
  );
}
